/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from 'app/lib/utils'

// Chrome's autofill background yields only to an opaque inset box-shadow, so this one repaints
// the field's own surface: autofilled and typed inputs are the same fill.
const inputVariants = cva(
  'block w-full rounded-lg border border-default bg-surface text-strong shadow-elevation-1 ring-offset-0 focus:border-default focus:outline-none focus:ring-0 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:border-default disabled:bg-surface-muted disabled:text-subtle read-only:cursor-not-allowed read-only:border-default read-only:bg-surface-muted read-only:text-subtle [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0_1000px_var(--surface)_inset,var(--elevation-1)] [&:-webkit-autofill]:[-webkit-text-fill-color:var(--fg-strong)]',
  {
    variants: {
      size: {
        sm: 'h-7 px-3 text-xs',
        md: 'h-9 px-4.25 py-3.5 text-body-sm',
        lg: 'h-12.5 px-4.25 py-3.5 text-body-sm',
      },
      variant: {
        default: '',
        bare: 'rounded-none border-0 bg-transparent px-0 shadow-none! read-only:bg-transparent',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  },
)

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'>, VariantProps<typeof inputVariants> {
  icon?: React.ReactNode
  /** Interactive adornment pinned inside the right edge, such as a password reveal toggle. */
  trailing?: React.ReactNode
  /** Classes for the positioning wrapper, where margins and width belong. */
  wrapperClassName?: string
  /** When true, wrapper shrinks to fit the input width instead of expanding to fill parent */
  inline?: boolean
}

function Input({
  className,
  wrapperClassName,
  type,
  icon,
  trailing,
  size,
  variant,
  inline,
  ref,
  ...props
}: InputProps) {
  return (
    <div className={cn('relative', inline ? 'w-fit' : 'w-full', wrapperClassName)}>
      <input
        type={type}
        className={cn(inputVariants({ size, variant }), icon && 'pl-10', trailing && 'pr-10', className)}
        ref={ref}
        {...props}
      />
      {/* Adornments render after the input so they paint over its background, and the static one
          ignores pointer events so a click anywhere in the box still lands on the field. */}
      {icon && (
        <span className="pointer-events-none absolute inset-y-0 left-2 flex w-6 items-center justify-center text-body-sm">
          {icon}
        </span>
      )}
      {trailing && <span className="absolute inset-y-0 right-2 flex items-center">{trailing}</span>}
    </div>
  )
}

export { Input, inputVariants }
